import React, { useState } from 'react';
import { Form, Spinner, Badge, Card, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetItems, usePatchItemsId, useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Item } from '../api/generated/model';
import { useQueryClient } from '@tanstack/react-query';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { RootState } from '../store';

const ItemsAdmin: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data, isLoading } = useGetItems({});
  const patchMutation = usePatchItemsId();
  const queryClient = useQueryClient();
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const items: Item[] = (data as any)?.data || [];

  const handleToggleField = async (item: Item, field: string, value: boolean) => {
    if (!item.id) return;
    try {
      await patchMutation.mutateAsync({
        id: item.id,
        data: [{ op: 'replace', path: `/${field}`, value }] as any,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/v1/items'] });
    } catch (err) {
      console.error('Failed to update item:', err);
    }
  };

  const columns: ColumnConfig<Item>[] = [
    { key: 'name', label: 'Name', width: 150, minWidth: 150, sortable: true },
    { key: 'type', label: 'Type', width: 100, sortable: true, renderCell: (item) => <Badge bg="secondary">{item.type || 'Unknown'}</Badge> },
    { key: 'sku', label: 'SKU', width: 100, sortable: true },
    { key: 'active', label: 'Active', width: 80, sortable: true, sortValue: (item) => item.active ? 1 : 0, renderCell: (item) => (
      <div className="text-center">{item.active ? <i className="bi bi-check-lg text-success"></i> : <i className="bi bi-x-lg text-danger"></i>}</div>
    ) },
    { key: 'requires_delivery', label: 'Requires Delivery', width: 120, sortable: true, sortValue: (item) => item.requires_delivery ? 1 : 0, renderCell: (item) => (
      <div className="text-center">
        <Form.Check
          id={`delivery-${item.id}`}
          type="checkbox"
          checked={item.requires_delivery !== false}
          onChange={(e) => { e.stopPropagation(); handleToggleField(item, 'requires_delivery', e.target.checked); }}
        />
      </div>
    ) },
    { key: 'counts_toward_net_revenue', label: 'Counts Toward Net Revenue', width: 150, sortable: true, sortValue: (item) => item.counts_toward_net_revenue ? 1 : 0, renderCell: (item) => (
      <div className="text-center">
        <Form.Check
          id={`revenue-${item.id}`}
          type="checkbox"
          checked={item.counts_toward_net_revenue !== false}
          onChange={(e) => { e.stopPropagation(); handleToggleField(item, 'counts_toward_net_revenue', e.target.checked); }}
        />
      </div>
    ) },
  ];

  if (isLoading) {
    return (
      <DashboardLayout title="Products & Services">
        <div className="text-center py-5">
          <Spinner animation="border" /> Loading items...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Products & Services" subtitle="Configure delivery tracking and net revenue settings" scrollable>
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <span className="fw-semibold">Products & Services</span>
        </Card.Header>
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={items}
            columns={columns}
            getRowKey={(item) => item.id || ''}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
            preferencesKey="items_admin"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            enableStickyHeader={true}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            emptyState={
              <Alert variant="info" className="m-3">
                No items found. Run a QuickBooks sync to populate items.
              </Alert>
            }
          />
        </Card.Body>
      </Card>
    </DashboardLayout>
  );
};

export default ItemsAdmin;
